/* ============================================================
   glow-loop-carousel.js — <glow-loop-carousel> קרוסלה מעגלית מבודדת.
   חולץ מ-glow-pdp.js (גל אינסטגרם-וול) לאסט משותף כדי ש-PDP
   (glow-related-products) וגם ההומפייג' (glow-instagram-wall) יצרכו
   אותו custom element בלי כפילות קוד ובלי לטעון את כל glow-pdp בהומפייג'.

   טכניקה: משכפלים את סט הפריטים המקורי לפני ואחרי (3 סטים), מתחילים בסט
   האמצעי, ובכל גלילה שחוצה גבול — jump ללא אנימציה לעותק המקביל (התוכן זהה,
   אז לא נראה "קצה"). RTL-aware דרך scrollBy (הדפדפן מטפל בכיוון).
   זהירות: לא נוגעים ב-this.dir/id/title/hidden (התנגשות DOM שקטה
   ששוברת web components — הלקח מ-arc-carousel). guard כפול למניעת
   הגדרה כפולה כשגם glow-pdp וגם glow-instagram-wall בעמוד.

   ביצועים: רוחב-הסט (#setW) וכיוון ה-RTL (#rtl) נמדדים פעם אחת (+ resize),
   לא בכל אירוע scroll — כדי לא להציף את הקונסול ב-[Violation] 'scroll'
   handler warnings (getComputedStyle/reflow בכל גלילת מומנטום).

   ניהול-מצב דינמי (#syncMode): ה-clones מנוהלים לפי breakpoint חי, לא
   פעם-אחת. בקיר-האינסטגרם ה-track הוא flex<750px (loop) ו-grid≥750px
   (masonry סטטי). אם הדף נטען צר (DevTools/חלון-צר) ואז מתרחב לדסקטופ,
   הפעם-אחת הישן השאיר clones שה-grid רינדר → כפל אריחים. עכשיו matchMedia
   מוסיף/מסיר clones בכל חציית-breakpoint. ב-PDP ה-track תמיד flex
   (enabled_on: product, אין grid) → תמיד "מובייל-מצב" → loop תמיד פעיל.

   04.10.2026 · data-glow-loop-defer (opt-in, קיר האינסטגרם בלבד): ה-clones
   נבנים רק כשהקרוסלה מתקרבת למסך (800px), ולא בטעינת הדף. במאמר הקיר יושב
   אחרי מאמר שלם, ושני הסטים הנוספים (56 אריחים עם הפוסטים החיים) נבנו לשווא
   אצל מי שלא גוללת עד אליו. בלי ה-attribute (PDP) ההתנהגות זהה לקודם.
   ============================================================ */
{
/* block scope: the file may be included by two sections on one page (the v2 product template has both the related
   products and the Instagram wall); a second run then neither redeclares the class nor redefines the element */
class GlowLoopCarousel extends HTMLElement {
  #setW = 0;
  #rtl = false;
  #resizeObs = null;
  #mql = null;
  #armed = true;
  #nearObs = null;

  connectedCallback() {
    this.track = this.querySelector('[data-glow-loop-track]');
    this.prevBtn = this.querySelector('[data-glow-loop-prev]');
    this.nextBtn = this.querySelector('[data-glow-loop-next]');
    if (!this.track) return;

    this.originals = Array.from(this.track.querySelectorAll('[data-glow-loop-item]'));
    this.loopReady = false;
    this.#rtl = getComputedStyle(this.track).direction === 'rtl';

    this.onPrev = this.#scrollPrev.bind(this);
    this.onNext = this.#scrollNext.bind(this);
    this.onScroll = this.#handleScroll.bind(this);
    this.prevBtn?.addEventListener('click', this.onPrev);
    this.nextBtn?.addEventListener('click', this.onNext);

    // מדידת רוחב-סט פעם אחת + על resize בלבד — לא בכל scroll. פעיל תמיד,
    // גם לפני שנבנו clones, כדי ש-#buildClones אחרי חציית-breakpoint ימדוד נכון.
    if ('ResizeObserver' in window) {
      this.#resizeObs = new ResizeObserver(() => this.#measure());
      this.#resizeObs.observe(this.track);
    }

    // מצב-ה-clones נגזר מ-breakpoint חי (flex=loop / grid=static), לא פעם-אחת.
    this.onModeChange = this.#syncMode.bind(this);
    this.#mql = window.matchMedia('(min-width: 750px)');
    this.#mql.addEventListener('change', this.onModeChange);

    if (this.hasAttribute('data-glow-loop-defer') && 'IntersectionObserver' in window) {
      this.#armed = false;
      this.#nearObs = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#nearObs?.disconnect();
        this.#nearObs = null;
        this.#armed = true;
        this.#syncMode();
      }, { rootMargin: '800px 0px' });
      this.#nearObs.observe(this);
    }

    this.#syncMode();
  }

  // API ציבורי: לקרוא אחרי שהוזרקו/הוסרו פריטים ל-track (למשל פוסטים חיים
  // בקיר האינסטגרם). מפרק clones ישנים, קורא מחדש את סט המקוריים לפי סדר
  // ה-DOM העדכני, ובונה מחדש לפי ה-breakpoint הנוכחי. אידמפוטנטי.
  refresh() {
    if (!this.track) return;
    if (this.loopReady) this.#teardownClones();
    this.originals = Array.from(
      this.track.querySelectorAll('[data-glow-loop-item]:not([data-glow-loop-clone])')
    );
    this.#syncMode();
  }

  disconnectedCallback() {
    this.prevBtn?.removeEventListener('click', this.onPrev);
    this.nextBtn?.removeEventListener('click', this.onNext);
    this.track?.removeEventListener('scroll', this.onScroll);
    this.#mql?.removeEventListener('change', this.onModeChange);
    this.#resizeObs?.disconnect();
    this.#nearObs?.disconnect();
    this.#nearObs = null;
    clearTimeout(this.jumpTimer);
  }

  // נקרא ב-connectedCallback ובכל חציית-breakpoint (matchMedia change).
  // desktop (grid) → מפרק clones ומכבה loop; mobile (flex) → בונה clones
  // ומדליק loop. אידמפוטנטי: לא בונה פעמיים ולא מפרק כשאין מה לפרק.
  #syncMode() {
    // desktop grid, אבל clones עדיין קיימים → הסר אותם (זה הבאג המתוקן).
    if (this.#isGridTrack()) {
      if (this.loopReady) this.#teardownClones();
      return;
    }
    // data-glow-loop-defer: עד שהקרוסלה מתקרבת למסך לא בונים clones.
    if (!this.#armed) return;
    // mobile flex + עדיין לא בנוי + מספיק פריטים → בנה loop.
    // עם <=2 אין מה ללופ — גלילה רגילה.
    if (!this.loopReady && this.originals.length >= 3) {
      this.#buildClones();
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
    }
  }

  // מפרק את כל ה-clones ומחזיר את ה-track למצב מקורי (grid/masonry).
  #teardownClones() {
    this.track.removeEventListener('scroll', this.onScroll);
    clearTimeout(this.jumpTimer);
    this.track.querySelectorAll('[data-glow-loop-clone]').forEach((c) => c.remove());
    this.loopReady = false;
    this.#setW = 0;
    this.track.scrollLeft = 0;
  }

  #buildClones() {
    // סט אחד לפני + סט אחד אחרי (clones, aria-hidden + inert לניווט/קורא-מסך)
    const before = document.createDocumentFragment();
    const after = document.createDocumentFragment();
    for (const el of this.originals) {
      const cloneBefore = el.cloneNode(true);
      const cloneAfter = el.cloneNode(true);
      for (const c of [cloneBefore, cloneAfter]) {
        c.setAttribute('aria-hidden', 'true');
        c.setAttribute('data-glow-loop-clone', '');
        c.querySelectorAll('a').forEach((a) => a.setAttribute('tabindex', '-1'));
      }
      before.appendChild(cloneBefore);
      after.appendChild(cloneAfter);
    }
    this.track.appendChild(after);
    this.track.insertBefore(before, this.track.firstChild);
    this.loopReady = true;

    // מתחילים בסט האמצעי (המקוריים) — jump ללא אנימציה.
    requestAnimationFrame(() => {
      this.#measure();
      this.#setPos(this.#setW);
    });
  }

  // רוחב סט מקורי אחד (המרחק לקפיצה בין העותקים) — מחושב פעם אחת,
  // נשמר ב-#setW. reflow יקר, לכן לא בכל scroll אלא רק בבנייה + resize.
  #measure() {
    if (!this.originals.length) { this.#setW = 0; return; }
    const first = this.originals[0];
    const style = getComputedStyle(this.track);
    const gap = parseFloat(style.columnGap || style.gap || '0') || 0;
    const itemW = first.getBoundingClientRect().width;
    this.#setW = (itemW + gap) * this.originals.length;
  }

  // scrollLeft ב-RTL הוא שלילי בדפדפנים מודרניים — עובדים עם ערך מוחלט.
  #getPos() {
    return Math.abs(this.track.scrollLeft);
  }

  #setPos(absPos) {
    this.track.scrollLeft = (this.#rtl ? -1 : 1) * absPos;
  }

  // דסקטופ קיר-האינסטגרם: ה-track הוא CSS grid → אין loop (masonry סטטי).
  // PDP/מובייל: flex → loop רגיל. נקרא בכל #syncMode (breakpoint חי).
  #isGridTrack() {
    return getComputedStyle(this.track).display === 'grid';
  }

  #step() {
    const item = this.track.querySelector('[data-glow-loop-item]');
    const base = item ? item.getBoundingClientRect().width : this.track.clientWidth * 0.8;
    return Math.round(base * 2);
  }

  #scrollPrev() {
    this.track.scrollBy({ left: -this.#step(), behavior: 'smooth' });
  }

  #scrollNext() {
    this.track.scrollBy({ left: this.#step(), behavior: 'smooth' });
  }

  // מיישרים לסט האמצעי כדי לשמור לופ אינסופי. שני מצבים:
  // 1. קרוב לקצה הפיזי (מתחת 0.25 או מעל 1.75 סטים) → recenter מיד — אחרת
  //    גלילת מומנטום מהירה "נתקעת" בקצה הממשי לפני שה-debounce מספיק לרוץ
  //    (הבאג שדווח: הגלילה נתקעת אחרי שמשחקים עם החצים).
  // 2. אחרת → debounce ~160ms: jump רק כשהגלילה נחה, בלי קפיצה מטרידה
  //    באמצע drag. משתמשים ב-#setW ה-cached (בלי reflow בכל scroll → אפס
  //    [Violation] warnings).
  #handleScroll() {
    if (!this.loopReady || this.#setW <= 0) return;
    const pos = this.#getPos();
    if (pos < this.#setW * 0.25 || pos > this.#setW * 1.75) {
      clearTimeout(this.jumpTimer);
      this.#recenter();
      return;
    }
    clearTimeout(this.jumpTimer);
    this.jumpTimer = setTimeout(() => this.#recenter(), 160);
  }

  #recenter() {
    const setW = this.#setW;
    if (setW <= 0) return;
    const pos = this.#getPos();
    // אזור המקוריים = [setW, 2*setW]. מחוץ לו → מקפיצים בחזרה פנימה (ללא אנימציה).
    if (pos < setW * 0.5) {
      this.#setPos(pos + setW);
    } else if (pos > setW * 1.5) {
      this.#setPos(pos - setW);
    }
  }
}

if (!customElements.get('glow-loop-carousel')) {
  customElements.define('glow-loop-carousel', GlowLoopCarousel);
}
}
